import { Position } from '@/types/player';

// Radar templates config ids
// team/men
export const TEAM_RADAR_TEMPLATE_CONFIG_IDS_MEN = {
  ATTACKING: '7c71065a-56a8-4715-a3cd-514c1e8cf0a1',
  DEFENDING: '0e167a28-9427-4225-96ae-3ab28f8126c7',
} as const;

// team/women
export const TEAM_RADAR_TEMPLATE_CONFIG_IDS_WOMEN = {
  ATTACKING: '31da3e20-bed5-48c7-a9a1-5c6d4bc090a2',
  DEFENDING: '57a3fcf1-6f6b-4adb-b177-3472f3004767',
} as const;

// player/men
export const PLAYER_RADAR_TEMPLATE_CONFIG_IDS_MEN = {
  STRIKER: '155d6da2-4b0f-47fe-8bf4-8cd6e527eea1',
  GOALKEEPER: '44b92184-ee65-44ab-9a28-7ae0a1fd3074',
  MIDFIELDER: 'd301b64b-a8f8-4711-a803-5a83fb5135f4',
  CENTRE_BACK: 'fb4937e9-ac68-4765-81c5-a97d72a7b252',
  ATTACKING_MIDFIELDER: '5525d340-6e3e-4325-867f-66e9fb26176f',
  FULL_BACK: '5c3676cb-10a2-42d9-a544-63a0c27a0d8e',
  WINGER: '80022d3e-df8c-4feb-add6-b63187c70733',
  PHYSICAL: '920aedfe-2243-4801-9257-c395577b1215',
} as const;

// player/women
export const PLAYER_RADAR_TEMPLATE_CONFIG_IDS_WOMEN = {
  STRIKER: 'a39ebb5d-e602-4018-ae7c-aab98b7e004a',
  GOALKEEPER: '1fad41c5-eb59-417c-b69e-03d604eea46e',
  MIDFIELDER: '3cc23267-92e2-4514-be4b-d4c569fc4721',
  CENTRE_BACK: 'a6ba28ca-16d4-4b66-9310-1246a8d10d22',
  ATTACKING_MIDFIELDER: '2c3a3a8e-d6fd-418b-a30d-3fb6190b3c26',
  FULL_BACK: 'b08c92b0-49e5-4c0b-8e4d-d5b9e1eae241',
  WINGER: '622ea990-d5a3-40e4-b69d-a69ddba0971c',
  PHYSICAL: '38dc10a3-4547-4d35-9584-4a7833ae4346',
} as const;

// Synthetic id (not a DB-persisted config) so the physical widget keys and scopes independently.
export const PHYSICAL_RADAR_WIDGET_CONFIG_ID = 'fe145cf1-86a9-4b36-9bf5-a0c7b21d26e6';

// These types make sure you update RADAR_TEMPLATE_ORDER whenever a new template is added to any of the enums above
type StatsbombRadarConfigIds =
  | (typeof TEAM_RADAR_TEMPLATE_CONFIG_IDS_MEN)[keyof typeof TEAM_RADAR_TEMPLATE_CONFIG_IDS_MEN]
  | (typeof TEAM_RADAR_TEMPLATE_CONFIG_IDS_WOMEN)[keyof typeof TEAM_RADAR_TEMPLATE_CONFIG_IDS_WOMEN]
  | (typeof PLAYER_RADAR_TEMPLATE_CONFIG_IDS_MEN)[keyof typeof PLAYER_RADAR_TEMPLATE_CONFIG_IDS_MEN]
  | (typeof PLAYER_RADAR_TEMPLATE_CONFIG_IDS_WOMEN)[keyof typeof PLAYER_RADAR_TEMPLATE_CONFIG_IDS_WOMEN];

type RadarTemplateOrder = {
  [K in StatsbombRadarConfigIds]: number;
};

export const RADAR_TEMPLATE_ORDER: RadarTemplateOrder = {
  [TEAM_RADAR_TEMPLATE_CONFIG_IDS_MEN.ATTACKING]: 1,
  [TEAM_RADAR_TEMPLATE_CONFIG_IDS_MEN.DEFENDING]: 2,

  [TEAM_RADAR_TEMPLATE_CONFIG_IDS_WOMEN.ATTACKING]: 1,
  [TEAM_RADAR_TEMPLATE_CONFIG_IDS_WOMEN.DEFENDING]: 2,

  [PLAYER_RADAR_TEMPLATE_CONFIG_IDS_MEN.GOALKEEPER]: 1,
  [PLAYER_RADAR_TEMPLATE_CONFIG_IDS_MEN.FULL_BACK]: 2,
  [PLAYER_RADAR_TEMPLATE_CONFIG_IDS_MEN.CENTRE_BACK]: 3,
  [PLAYER_RADAR_TEMPLATE_CONFIG_IDS_MEN.MIDFIELDER]: 4,
  [PLAYER_RADAR_TEMPLATE_CONFIG_IDS_MEN.ATTACKING_MIDFIELDER]: 5,
  [PLAYER_RADAR_TEMPLATE_CONFIG_IDS_MEN.WINGER]: 6,
  [PLAYER_RADAR_TEMPLATE_CONFIG_IDS_MEN.STRIKER]: 7,
  [PLAYER_RADAR_TEMPLATE_CONFIG_IDS_MEN.PHYSICAL]: 8,

  [PLAYER_RADAR_TEMPLATE_CONFIG_IDS_WOMEN.GOALKEEPER]: 1,
  [PLAYER_RADAR_TEMPLATE_CONFIG_IDS_WOMEN.FULL_BACK]: 2,
  [PLAYER_RADAR_TEMPLATE_CONFIG_IDS_WOMEN.CENTRE_BACK]: 3,
  [PLAYER_RADAR_TEMPLATE_CONFIG_IDS_WOMEN.MIDFIELDER]: 4,
  [PLAYER_RADAR_TEMPLATE_CONFIG_IDS_WOMEN.ATTACKING_MIDFIELDER]: 5,
  [PLAYER_RADAR_TEMPLATE_CONFIG_IDS_WOMEN.WINGER]: 6,
  [PLAYER_RADAR_TEMPLATE_CONFIG_IDS_WOMEN.STRIKER]: 7,
  [PLAYER_RADAR_TEMPLATE_CONFIG_IDS_WOMEN.PHYSICAL]: 8,
};

export const PHYSICAL_RADAR_TEMPLATE_CONFIG_IDS: ReadonlySet<string> = new Set([
  PLAYER_RADAR_TEMPLATE_CONFIG_IDS_MEN.PHYSICAL,
  PLAYER_RADAR_TEMPLATE_CONFIG_IDS_WOMEN.PHYSICAL,
]);

export const POSITION_TEMPLATE_MAP_MEN: Record<Position, string> = {
  lcf: PLAYER_RADAR_TEMPLATE_CONFIG_IDS_MEN.STRIKER,
  cf: PLAYER_RADAR_TEMPLATE_CONFIG_IDS_MEN.STRIKER,
  rcf: PLAYER_RADAR_TEMPLATE_CONFIG_IDS_MEN.STRIKER,

  lm: PLAYER_RADAR_TEMPLATE_CONFIG_IDS_MEN.WINGER,
  rm: PLAYER_RADAR_TEMPLATE_CONFIG_IDS_MEN.WINGER,
  lw: PLAYER_RADAR_TEMPLATE_CONFIG_IDS_MEN.WINGER,
  rw: PLAYER_RADAR_TEMPLATE_CONFIG_IDS_MEN.WINGER,

  lam: PLAYER_RADAR_TEMPLATE_CONFIG_IDS_MEN.ATTACKING_MIDFIELDER,
  cam: PLAYER_RADAR_TEMPLATE_CONFIG_IDS_MEN.ATTACKING_MIDFIELDER,
  ram: PLAYER_RADAR_TEMPLATE_CONFIG_IDS_MEN.ATTACKING_MIDFIELDER,

  ldm: PLAYER_RADAR_TEMPLATE_CONFIG_IDS_MEN.MIDFIELDER,
  cdm: PLAYER_RADAR_TEMPLATE_CONFIG_IDS_MEN.MIDFIELDER,
  rdm: PLAYER_RADAR_TEMPLATE_CONFIG_IDS_MEN.MIDFIELDER,
  lcm: PLAYER_RADAR_TEMPLATE_CONFIG_IDS_MEN.MIDFIELDER,
  cm: PLAYER_RADAR_TEMPLATE_CONFIG_IDS_MEN.MIDFIELDER,
  rcm: PLAYER_RADAR_TEMPLATE_CONFIG_IDS_MEN.MIDFIELDER,

  lcb: PLAYER_RADAR_TEMPLATE_CONFIG_IDS_MEN.CENTRE_BACK,
  cb: PLAYER_RADAR_TEMPLATE_CONFIG_IDS_MEN.CENTRE_BACK,
  rcb: PLAYER_RADAR_TEMPLATE_CONFIG_IDS_MEN.CENTRE_BACK,

  lb: PLAYER_RADAR_TEMPLATE_CONFIG_IDS_MEN.FULL_BACK,
  rb: PLAYER_RADAR_TEMPLATE_CONFIG_IDS_MEN.FULL_BACK,
  lwb: PLAYER_RADAR_TEMPLATE_CONFIG_IDS_MEN.FULL_BACK,
  rwb: PLAYER_RADAR_TEMPLATE_CONFIG_IDS_MEN.FULL_BACK,

  gk: PLAYER_RADAR_TEMPLATE_CONFIG_IDS_MEN.GOALKEEPER,
};

export const POSITION_TEMPLATE_MAP_WOMEN: Record<Position, string> = {
  lcf: PLAYER_RADAR_TEMPLATE_CONFIG_IDS_WOMEN.STRIKER,
  cf: PLAYER_RADAR_TEMPLATE_CONFIG_IDS_WOMEN.STRIKER,
  rcf: PLAYER_RADAR_TEMPLATE_CONFIG_IDS_WOMEN.STRIKER,

  lm: PLAYER_RADAR_TEMPLATE_CONFIG_IDS_WOMEN.WINGER,
  rm: PLAYER_RADAR_TEMPLATE_CONFIG_IDS_WOMEN.WINGER,
  lw: PLAYER_RADAR_TEMPLATE_CONFIG_IDS_WOMEN.WINGER,
  rw: PLAYER_RADAR_TEMPLATE_CONFIG_IDS_WOMEN.WINGER,

  lam: PLAYER_RADAR_TEMPLATE_CONFIG_IDS_WOMEN.ATTACKING_MIDFIELDER,
  cam: PLAYER_RADAR_TEMPLATE_CONFIG_IDS_WOMEN.ATTACKING_MIDFIELDER,
  ram: PLAYER_RADAR_TEMPLATE_CONFIG_IDS_WOMEN.ATTACKING_MIDFIELDER,

  ldm: PLAYER_RADAR_TEMPLATE_CONFIG_IDS_WOMEN.MIDFIELDER,
  cdm: PLAYER_RADAR_TEMPLATE_CONFIG_IDS_WOMEN.MIDFIELDER,
  rdm: PLAYER_RADAR_TEMPLATE_CONFIG_IDS_WOMEN.MIDFIELDER,
  lcm: PLAYER_RADAR_TEMPLATE_CONFIG_IDS_WOMEN.MIDFIELDER,
  cm: PLAYER_RADAR_TEMPLATE_CONFIG_IDS_WOMEN.MIDFIELDER,
  rcm: PLAYER_RADAR_TEMPLATE_CONFIG_IDS_WOMEN.MIDFIELDER,

  lcb: PLAYER_RADAR_TEMPLATE_CONFIG_IDS_WOMEN.CENTRE_BACK,
  cb: PLAYER_RADAR_TEMPLATE_CONFIG_IDS_WOMEN.CENTRE_BACK,
  rcb: PLAYER_RADAR_TEMPLATE_CONFIG_IDS_WOMEN.CENTRE_BACK,

  lb: PLAYER_RADAR_TEMPLATE_CONFIG_IDS_WOMEN.FULL_BACK,
  rb: PLAYER_RADAR_TEMPLATE_CONFIG_IDS_WOMEN.FULL_BACK,
  lwb: PLAYER_RADAR_TEMPLATE_CONFIG_IDS_WOMEN.FULL_BACK,
  rwb: PLAYER_RADAR_TEMPLATE_CONFIG_IDS_WOMEN.FULL_BACK,

  gk: PLAYER_RADAR_TEMPLATE_CONFIG_IDS_WOMEN.GOALKEEPER,
};

export const MIN_SELECTED_METRICS = 5;
export const MAX_SELECTED_METRICS = 13;
